import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useQuery } from '@apollo/client'
import { Query } from '@apollo/client/react/components'
import { Button, Dropdown, Icon, Layout, Popconfirm } from 'antd'
import gql from 'graphql-tag'
import _ from 'lodash'
import { withRouter } from 'react-router-dom'
import styled from 'styled-components'

import brainLogo from '../../../../../../assets/logos/brain-logo.svg'
import { getWhiteColor } from '../../../../../../components/hocs/withOverriddenTheme'
import errorService from '../../../../../../utils/analytics/error'
import { logout } from '../../../../../../utils/authentication'
import { pickWithInitialState } from '../../../../../../utils/data/utils'
// import { useSendUserData } from '../../../../../../utils/fullstory/hooks/useSendUserData'
import { AUTHENTICATED_ROUTES } from '../../../../../../utils/routing/routes'
import { getDemoDimNames } from '../../../../../../utils/superUserDataInteraction'

import HeaderLinkButton from './components/HeaderLinkButton'

const { Header } = Layout

const SButton = styled(/** @type {any} */ (Button))`
  margin: 0px 8px 0px 8px;

  &:hover,
  &:focus {
    color: #33628e !important;
  }
`

const SHeader = styled(/** @type {any} */ (Header))`
  background-color: ${getWhiteColor} !important;
  border-bottom: 3px solid #eee;
`

const LogoContainer = styled.button`
  border: none;
  cursor: pointer;
  padding: 0;
`

const MenuItemsContainer = styled.div`
  @media (max-width: 992px) {
    display: none;
  }
`

const DropdownMenuItemsContainer = styled.div`
  @media (min-width: 992px) {
    display: none;
  }
`

const MobileMenuOverlay = styled.div`
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
  min-width: 220px;
  padding: 8px 0;
`

const MobileMenuItem = styled.button`
  background: transparent;
  border: none;
  color: rgba(0, 0, 0, 0.85);
  cursor: pointer;
  display: block;
  padding: 10px 16px;
  text-align: left;
  width: 100%;

  &:hover,
  &:focus {
    background: #f5f5f5;
    outline: none;
  }
`

const MobileMenuDivider = styled.div`
  border-top: 1px solid #f0f0f0;
  margin: 8px 0;
`

const SImg = styled.img`
  max-width: 200px;
`

export const universityClientUserProfileQuery = gql`
  query UniversityClientUserProfile {
    universityClientUserProfile {
      canUseDataExports
      documents {
        id
        name
      }
      email
      firstName
      id
      impersonatorId
      isAdmin
      isSystemAdmin
      lastName
      clientName
      clientId
      createdAt
    }
  }
`

const getDataFields = (data = {}) => {
  const universityClientUserProfile = _.get(data, 'universityClientUserProfile')
  return pickWithInitialState(universityClientUserProfile, [
    ['email', ''],
    ['canUseDataExports', false],
    ['documents', []],
    ['isAdmin', false],
    ['isSystemAdmin', false],
    ['id', 'No User Id Available'],
  ])
}

const AuthenticatedHeader = props => {
  const navigate = useCallback(
    route => {
      props.history.push(route)
    },
    [props.history],
  )

  const onLogout = useCallback(() => {
    logout()
  }, [])

  const logoutWrapperRef = useRef(null)
  const mobileMenuButtonRef = useRef(null)

  const onLogoutCancel = useCallback(() => {
    const btn = logoutWrapperRef.current?.querySelector('button')
    if (btn instanceof HTMLElement) btn.focus()
  }, [])

  const onLogoutPopconfirmVisibleChange = useCallback(visible => {
    if (!visible) return
    requestAnimationFrame(() => {
      const popover = document.querySelector('.logout-popconfirm')
      if (!(popover instanceof HTMLElement)) return

      const inner = popover.querySelector('.ant-popover-inner')
      if (inner instanceof HTMLElement) {
        inner.setAttribute('role', 'dialog')
        inner.setAttribute('aria-label', 'Confirm log out')
        inner.setAttribute('data-testid', 'logout-popover')
      }

      const icon = popover.querySelector('.ant-popover-message > .anticon')
      if (icon instanceof HTMLElement) {
        icon.setAttribute('aria-hidden', 'true')
        icon.removeAttribute('aria-label')
      }

      const title = popover.querySelector('.ant-popover-message-title')
      if (title instanceof HTMLElement) {
        title.setAttribute('tabindex', '-1')
        title.focus()
      }
    })
  }, [])

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const onMobileMenuVisibleChange = useCallback(visible => {
    setIsMobileMenuOpen(visible)
    if (!visible && mobileMenuButtonRef.current instanceof HTMLElement) {
      mobileMenuButtonRef.current.focus()
    }
  }, [])

  const onMobileMenuNavigate = useCallback(
    route => {
      setIsMobileMenuOpen(false)
      navigate(route)
    },
    [navigate],
  )

  const onMobileMenuLogout = useCallback(() => {
    setIsMobileMenuOpen(false)
    onLogout()
  }, [onLogout])

  useEffect(() => {
    if (!isMobileMenuOpen) return

    requestAnimationFrame(() => {
      const firstMenuItem = document.querySelector('.mobile-nav-dropdown [data-mobile-menu-item]')
      if (firstMenuItem instanceof HTMLElement) firstMenuItem.focus()
    })
  }, [isMobileMenuOpen])

  const demoDimNames = getDemoDimNames()

  const { data } = useQuery(universityClientUserProfileQuery)

  const { canUseDataExports, isAdmin, isSystemAdmin, id, email } = getDataFields(data)

  errorService.setUserContext({ email: email, id: id })

  // useSendUserData(data)

  return (
    <SHeader style={{ padding: 0 }}>
      <div className="header-container" role="banner">
        <div className="left-nav-wrapper">
          <LogoContainer
            type="button"
            onClick={() => navigate(AUTHENTICATED_ROUTES.DASHBOARD)}
            className="brand-logo"
            aria-label="Go to Dashboard"
          >
            <SImg alt="" src={brainLogo} />
          </LogoContainer>
          <MenuItemsContainer>
            <HeaderLinkButton route={AUTHENTICATED_ROUTES.DASHBOARD}>
              <Icon type="database" style={{ fontSize: 20 }} aria-hidden="true" />
              Dashboard
            </HeaderLinkButton>
            {canUseDataExports && (
              <HeaderLinkButton route={AUTHENTICATED_ROUTES.DATA_FILES}>
                <Icon type="database" style={{ fontSize: 20 }} aria-hidden="true" />
                Data
              </HeaderLinkButton>
            )}
            {isAdmin && (
              <HeaderLinkButton route={AUTHENTICATED_ROUTES.MANAGE_TEAM}>
                <Icon type="team" style={{ fontSize: 20 }} aria-hidden="true" />
                Team
              </HeaderLinkButton>
            )}
            {isSystemAdmin && (
              <HeaderLinkButton route={AUTHENTICATED_ROUTES.DEMO}>
                <Icon
                  type="project"
                  theme={demoDimNames && !!demoDimNames.length ? 'twoTone' : 'outlined'}
                  twoToneColor="#eb2f96"
                  style={{ fontSize: 20 }}
                  aria-hidden="true"
                />
                Demo
              </HeaderLinkButton>
            )}
          </MenuItemsContainer>
        </div>
        <Query query={universityClientUserProfileQuery}>
          {({ data }) => {
            const { isAdmin } = getDataFields(data)

            const buttons = [
              {
                icon: 'user',
                route: AUTHENTICATED_ROUTES.MY_ACCOUNT,
                title: 'My Account',
              },
              {
                icon: 'question-circle',
                route: AUTHENTICATED_ROUTES.HELP,
                title: 'Help',
              },
            ]
            return (
              <React.Fragment>
                <MenuItemsContainer>
                  {buttons.map(({ title, icon, ...rest }) => (
                    <HeaderLinkButton key={title} {...rest}>
                      <Icon type={icon} aria-hidden="true" />
                      {title}
                    </HeaderLinkButton>
                  ))}
                  <Popconfirm
                    onConfirm={onLogout}
                    title="Are you sure you want to log out?"
                    okText="Yes"
                    placement="rightBottom"
                    overlayClassName="logout-popconfirm"
                    getPopupContainer={trigger => trigger.parentElement}
                    onVisibleChange={onLogoutPopconfirmVisibleChange}
                    onCancel={onLogoutCancel}
                  >
                    {/* Need this span to focus the button when a screen reader user clicks 'cancel' */}
                    <span ref={logoutWrapperRef}>
                      <SButton
                        type="link"
                        aria-label="Log out"
                        data-testid="logout"
                        style={{ height: 61 }}
                      >
                        <Icon
                          type="upload"
                          aria-hidden="true"
                          style={{ fontSize: 20, transform: 'rotate(90deg)' }}
                        />
                      </SButton>
                    </span>
                  </Popconfirm>
                </MenuItemsContainer>
                <DropdownMenuItemsContainer>
                  <Dropdown
                    trigger={['click']}
                    visible={isMobileMenuOpen}
                    onVisibleChange={onMobileMenuVisibleChange}
                    overlayClassName="mobile-nav-dropdown"
                    overlay={
                      <MobileMenuOverlay role="menu" aria-label="Mobile navigation menu">
                        {isAdmin && (
                          <MobileMenuItem
                            type="button"
                            role="menuitem"
                            data-mobile-menu-item
                            onClick={() => onMobileMenuNavigate(AUTHENTICATED_ROUTES.MANAGE_TEAM)}
                          >
                            Manage Team
                          </MobileMenuItem>
                        )}
                        {buttons.map(({ route, title }) => (
                          <MobileMenuItem
                            key={route}
                            type="button"
                            role="menuitem"
                            data-mobile-menu-item
                            onClick={() => onMobileMenuNavigate(route)}
                          >
                            {title}
                          </MobileMenuItem>
                        ))}
                        <MobileMenuDivider />
                        <MobileMenuItem
                          type="button"
                          role="menuitem"
                          data-mobile-menu-item
                          onClick={onMobileMenuLogout}
                        >
                          Log Out
                        </MobileMenuItem>
                      </MobileMenuOverlay>
                    }
                  >
                    <SButton
                      ref={mobileMenuButtonRef}
                      icon="menu"
                      aria-label="Open navigation menu"
                      aria-haspopup="true"
                      aria-expanded={isMobileMenuOpen}
                    />
                  </Dropdown>
                </DropdownMenuItemsContainer>
              </React.Fragment>
            )
          }}
        </Query>
      </div>
    </SHeader>
  )
}

export default withRouter(AuthenticatedHeader)
